import React from 'react';
import { Link } from 'react-router';
import SEO from '../components/SEO';
import GlowCard from '../components/GlowCard';
import AIImage from '../components/AIImage';
import { trackEvent } from '../components/analytics';

const SmsAirlines: React.FC = () => {
  return (
    <div className="animate-fade-in-up">
      <SEO
        title="Airline Safety Management System Software"
        description="Airline safety management system software for occurrence reporting, risk control, CAPA, SPIs, audits, and regulator-ready evidence."
      />

      <section className="py-24 bg-slate-950 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <nav className="flex items-center space-x-2 text-slate-500 text-sm mb-6 uppercase tracking-widest">
              <span>Modules</span>
              <span>/</span>
              <span className="text-success">SMS</span>
              <span>/</span>
              <span>Airlines</span>
            </nav>
            <h1 className="text-5xl lg:text-7xl font-bold font-heading mb-8 leading-tight">
              Airline safety management
              <span className="block text-success">system software.</span>
            </h1>
            <p className="text-xl text-slate-400 mb-6 leading-relaxed">
              Turn airline safety reporting into governed actions, defensible risk decisions, and regulator-ready outputs.
            </p>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-10">
              Built for operational teams, accountable executives, and audit readiness.
            </p>
            <Link
              to="/contact"
              className="inline-flex px-10 py-4 bg-accent text-primary font-bold rounded-xl"
              onClick={() => trackEvent('demo_request', { form_id: 'sms_airlines_hero_demo', module: 'sms' })}
            >
              Request an Airline SMS Walkthrough
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 relative p-1 bg-slate-800 rounded-2xl overflow-hidden border border-white/10">
              <AIImage
                prompt="A modern airline safety oversight dashboard with structured evidence panels, risk matrix tiles, and audit-ready status lanes, dark interface, professional tone"
                alt="Airline SMS overview"
                className="w-full opacity-95"
                aspectRatio="16:9"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">Operational Coverage</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Core SMS Records</h3>
              <p className="text-slate-400 text-sm">
                Hazards, occurrences, change requests, audits, and CAPA tracked with evidence and status workflows.
              </p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Risk Assessment Engine</h3>
              <p className="text-slate-400 text-sm">
                Configurable risk matrix with multi-hazard scoring and risk-level classification.
              </p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Mitigation Planning</h3>
              <p className="text-slate-400 text-sm">
                Action tracking with due dates and CAPA linkage for hazard controls.
              </p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">CAPA Management</h3>
              <p className="text-slate-400 text-sm">
                Action ownership, verification, and effectiveness reviews.
              </p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Audit Program and Findings</h3>
              <p className="text-slate-400 text-sm">
                Audit schedules, finding severity, root cause, and closure criteria.
              </p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Safety Performance (SPIs)</h3>
              <p className="text-slate-400 text-sm">
                SPI governance, lineage, evaluations, threshold changes, and breach alerts.
              </p>
            </GlowCard>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 bg-slate-950/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">Reporting, Deadlines, and Evidence</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Reportability and SLA Tracking</h3>
              <p className="text-slate-400 text-sm">
                ICAO, EASA, FAA, and EU 376 reportability logic with 72-hour deadline tracking.
              </p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Dashboards and Evidence</h3>
              <p className="text-slate-400 text-sm">
                KPI dashboards, snapshots, PDF exports, and audit-ready outputs.
              </p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Compliance Packs and Exports</h3>
              <p className="text-slate-400 text-sm">
                Evidence bundles and regulator submissions packaged as compliance exports.
              </p>
            </GlowCard>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">Operational Engagement</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Safety Promotion</h3>
              <p className="text-slate-400 text-sm">
                Bulletins, campaigns, lessons learned, and acknowledgement tracking.
              </p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Training and Competency</h3>
              <p className="text-slate-400 text-sm">
                Training triggers, requests, effectiveness reviews, and training evidence bundles.
              </p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">SMS Public Portal</h3>
              <p className="text-slate-400 text-sm">
                Branded domain for external users with assigned document access and OTP login.
              </p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Mobile App (Planned)</h3>
              <p className="text-slate-400 text-sm">
                Offline reporting, evidence capture, secure sync, and push alerts.
              </p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Access and Security</h3>
              <p className="text-slate-400 text-sm">
                Access profiles, access logs, reporter protection, and just culture controls.
              </p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Compliance Coverage</h3>
              <p className="text-slate-400 text-sm">
                ICAO Annex 19 and 13, EASA Part-ORO, FAA Part 830, EU 376/2014, GACAR, and UK CAA with regime tagging.
              </p>
            </GlowCard>
          </div>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <Link
            to="/contact"
            className="inline-flex px-10 py-4 bg-accent text-primary font-bold rounded-xl"
            onClick={() => trackEvent('demo_request', { form_id: 'sms_airlines_cta_walkthrough', module: 'sms' })}
          >
            Request an Airline SMS Walkthrough
          </Link>
          <p className="text-slate-400 text-sm mt-4">
            Focused on evidence, deadlines, and airline operational oversight.
          </p>
        </div>
      </section>
    </div>
  );
};

export default SmsAirlines;
